import { Inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, catchError, map, Observable, of, retry, Subject, switchMap, throwError, delay } from 'rxjs';
import { environment } from '../../environments/environment';
import { WebStorage } from './web-storage';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Auth } from './auth';

@Injectable({
  providedIn: 'root',
})
export class Sso {

  public $login = new Subject<any>();
  public $loginResponse = new Subject<any>();
  public $logout = new Subject<boolean>();
  public $refreshToken = new Subject<boolean>();
  public $user = new Subject<any>();

  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;
  retryCount = 2;

  private dataSource = new BehaviorSubject<any>(null);
  customData$ = this.dataSource.asObservable();

  apiUrl: string = '';

  constructor(private http: HttpClient, private webStorageService: WebStorage,
    private msalService: MsalService, private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration) {
    this.$refreshToken.subscribe((res: any) => {
      if (environment.name.toLowerCase() == 'dev' || environment.name.toLowerCase() == 'local') {
        this.getRefreshToken().subscribe((res: any) => {
          sessionStorage.setItem('_t', res.token);
          sessionStorage.setItem('_rt', res.refresh_token);
        });
      } else {
        this.loginRedirect();
      }
    });

    this.$loginResponse.subscribe((res: any) => {
      //SSO Disabled Flow
      //this.webStorageService.setKey(environment.sk);
      //this.webStorageService.storeItem('access_token', res?.token ?? '');
      //this.webStorageService.storeInLocal('user_details', JSON.stringify(res));

      this.webStorageService.setKey(environment.sk);
      this.webStorageService.storeItem('cu', res.user);
      sessionStorage.setItem('_t', res.token);
      sessionStorage.setItem('_rt', res.refresh_token);
      // this.loaderService.hide();
      this.$user.next(res.user);
    });

    this.$login.subscribe((res: any) => {
      //SSO Enabled Flow
      if (res) {
        this.webStorageService.setKey(environment.sk);
        sessionStorage.setItem('_t', res.idToken); // idToken is encrypted from response itself, so no need to encrypt again
        const cu = res.account;
        this.msalService.instance.setActiveAccount(cu);
        let unArray = this.generateUNarray(cu.name);
        let ud = {
          eid: this.generateEId(cu.username),
          email: cu.idTokenClaims.email,
          name: unArray[1] + ' ' + unArray[0],
          roles: cu.idTokenClaims.roles || [],
          role: cu.idTokenClaims.roles?.length === 1 ? cu.idTokenClaims.roles[0].toLowerCase() : null,
        }
        this.webStorageService.storeItem('cu', ud);
        this.$user.next(ud);
      }
    });

    this.$logout.subscribe((res: any) => {
      if (environment.name.toLowerCase() == 'dev' || environment.name.toLowerCase() == 'local') {
        sessionStorage.clear();
        localStorage.clear();
        this.$user.next(null);
      } else {
        sessionStorage.clear();
        localStorage.clear();
        this.$user.next(null);
        // this.msalService.logoutRedirect();
        sessionStorage.setItem('isLoggingOut', 'true');
        this.router.navigate(['login']);
      }
    });

    this.currentUserSubject = new BehaviorSubject<any>(this.webStorageService.fetchFromLocal('user')! || '{}');
    this.currentUser = this.currentUserSubject.asObservable();
    this.apiUrl = environment.apiUrl;
  }

  setUserAccess(res: any) {
    this.webStorageService.setKey(environment.sk);
    sessionStorage.setItem('_t', res.access_token); // idToken is encrypted from response itself, so no need to encrypt again
    let ud = {
      eid: res.eid,
      email: res.email,
      name: res.name,
      roles: res.role.split(',') || [],
      role: res.role,
    }
    console.log('Roles from token claims:', ud.roles);
    this.webStorageService.storeItem('cu', ud);
    this.$user.next(ud);
  }

  getRefreshToken() {
    let refreshToken = sessionStorage.getItem('_rt');
    let user = this.webStorageService.retrieveItem('cu');
    let obj = {
      email: user.email_id,
      token: '',  
      refreshtoken: refreshToken,
    };
    return this.http.post<any>(`${environment.apiUrl}prodrefreshtoken`, obj);
  }
    
  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      console.log('msalGuardConfig.authRequest::::::', this.msalGuardConfig.authRequest);
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      console.log('No authRequest found in MSAL Guard Config');
      this.msalService.instance
        .handleRedirectPromise()
        .then((response: any) => {
          if (response) {
            // Handle the response after redirect
            // if (response.responseType === 'code') {
            if (response.idToken && response.idToken !== '') {
              //this.loaderService.display(false);
              this.$login.next(response);
            }
          }
        })
        .catch((error) => {
          console.error('Redirect error:', error);
        });
    }
  }

  async initialize() {
    await this.msalService.instance.initialize();
    console.log('Initializing');
    this.msalService.instance
      .handleRedirectPromise()
      .then((response: any) => {
        if (response) {
          // Handle the response after redirect
          // console.log('initialize-handleRedirectPromise::::' + JSON.stringify(response));
          // if (response.responseType === 'code') {
          if (response.idToken && response.idToken !== '') {
            //this.loaderService.display(false);
            this.$login.next(response);
          }
        }
      })
      .catch((error) => {
        console.error('Redirect error:', error);
      });
  }
  generateUNarray(name: string): Array<string> {
    if (name && name != '') {
      name = name.toLowerCase().replace(' (cognizant)', '');
      return name.split(',').map((item: string) => item.trim());
    } else {
      return ['', ''];
    }
  }
  generateEId(email: string): string {
    if (email && email != '') {
      let empid = email.replace(/@cognizant.com/i, '');
      return empid.trim();
    } else {
      return '';
    }
  }
}
